import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { apiErrorMessage } from "../lib/inventoryApi";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { formatIDR } from "../lib/format";
import { toast } from "sonner";
import { mintaTeks } from "../lib/dialogAplikasi";
import Hint from "../components/etams/Hint";
import { useDaftar } from "../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../components/etams/PalangDaftar";

// Label jenis opsi untuk layar (K200 butir 6 — kode mentah tidak tampil).
const LABEL_JENIS_OPSI = { LENGAN: "Lengan", POLA: "Pola", UKURAN: "Ukuran", PAKET_WAKTU: "Paket waktu" };

/* K177 1.1/3.3 — MASTER OPSI & TAMBAHAN (`opsi_harga`). Owner mengubah semua region (baris "Semua
   region" maupun baris region); Kepala Cabang hanya tarif region sendiri — baris region menang atas
   baris umum saat FA ditagih (`services/opsi_berbayar.pilih_tarif`). Tidak ada hapus: nonaktifkan.
   Penjaga sebenarnya di backend (/api/master/opsi-harga); tombol di sini hanya cermin. */

export default function MasterOpsiHarga() {
  const { roleCode, profile } = useAuth();
  const [data, setData] = useState({ items: [], bisa_tulis: false });
  const [sibuk, setSibuk] = useState(null);
  const [baru, setBaru] = useState({ jenis_opsi: "LENGAN", kode: "", label: "", tambahan_idr: "" });
  // Pola daftar seragam (28 Sep 2026) — master KECIL: cari/urut/halaman di klien; batas baris diawasi
  // `backend/tests/test_batas_master_kecil_lokal.py`.
  const daftar = useDaftar(data.items || [], {
    kunciCari: ["label", "kode", (o) => LABEL_JENIS_OPSI[o.jenis_opsi]],
    kolom: [
      { kunci: "jenis_opsi", judul: "Jenis", ambil: (o) => LABEL_JENIS_OPSI[o.jenis_opsi] || o.jenis_opsi },
      { kunci: "label", judul: "Opsi" }, { kunci: "kode", judul: "Kode" },
      { kunci: "berlaku", judul: "Berlaku", ambil: (o) => (o.region_id ? (o.region_id === profile?.region_id ? "Wilayah Anda" : "Wilayah lain") : "Semua wilayah") },
      { kunci: "tambahan_idr", judul: "Tambahan per lembar (IDR)" },
      { kunci: "aktif", judul: "Status", ambil: (o) => (o.aktif ? "Aktif" : "Nonaktif") },
    ],
  });

  const load = useCallback(async () => {
    try { const r = await api.get("/master/opsi-harga"); setData(r.data); }
    catch (e) { toast.error(apiErrorMessage(e, "Gagal memuat opsi.")); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const bolehUbah = (o) => roleCode === "OWNER" || (roleCode === "KEPALA_CABANG" && o.region_id && o.region_id === profile?.region_id);

  async function ubah(o, badan, pesan) {
    setSibuk(o.id);
    try { await api.patch(`/master/opsi-harga/${o.id}`, badan); toast.success(pesan); load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Perubahan ditolak.")); }
    finally { setSibuk(null); }
  }
  async function ubahTarif(o) {
    const v = await mintaTeks(`Tambahan per lembar untuk "${o.label}" (Rp):`, { awal: String(o.tambahan_idr), multiBaris: false });
    if (v === null) return;
    const n = Number(v);
    if (!Number.isInteger(n) || n < 0) { toast.error("Isi angka rupiah bulat ≥ 0."); return; }
    ubah(o, { tambahan_idr: n }, "Tarif diubah.");
  }
  async function tambah() {
    setSibuk("baru");
    try {
      await api.post("/master/opsi-harga", { ...baru, kode: baru.kode.trim().toUpperCase(), tambahan_idr: Number(baru.tambahan_idr) });
      toast.success("Opsi ditambahkan."); setBaru({ ...baru, kode: "", label: "", tambahan_idr: "" }); load();
    } catch (e) { toast.error(apiErrorMessage(e, "Opsi tidak bisa ditambahkan.")); }
    finally { setSibuk(null); }
  }

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="master-opsi-harga">
      <div>
        <h1 className="text-xl font-semibold">Opsi &amp; Tambahan Harga</h1>
        <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
          Tambahan rupiah per lembar untuk opsi berbayar.
          <Hint>Baris wilayah mengalahkan baris "Semua wilayah" untuk FA wilayah itu.{roleCode === "KEPALA_CABANG" && " Anda hanya bisa mengubah atau menambah tarif untuk wilayah Anda."}</Hint>
        </p>
      </div>

      {data.bisa_tulis && (
        <Card className="p-3 flex flex-wrap gap-2 items-end" data-testid="oh-baru">
          <label className="text-xs">Jenis
            <select className="block rounded-md border bg-background px-2 py-2 text-sm" value={baru.jenis_opsi}
                    onChange={e => setBaru({ ...baru, jenis_opsi: e.target.value })}>
              {["LENGAN", "POLA", "UKURAN", "PAKET_WAKTU"].map(j => <option key={j} value={j}>{LABEL_JENIS_OPSI[j]}</option>)}
            </select>
          </label>
          <Input className="w-44" placeholder="KODE (mis. LENGAN_PANJANG)" value={baru.kode} onChange={e => setBaru({ ...baru, kode: e.target.value })} />
          <Input className="w-48" placeholder="Label" value={baru.label} onChange={e => setBaru({ ...baru, label: e.target.value })} />
          <Input className="w-32" type="number" min={0} placeholder="Rp / lembar" value={baru.tambahan_idr} onChange={e => setBaru({ ...baru, tambahan_idr: e.target.value })} />
          <Button disabled={sibuk === "baru" || baru.kode.trim().length < 2 || baru.label.trim().length < 2 || baru.tambahan_idr === ""}
                  onClick={tambah} data-testid="oh-tambah">Tambah</Button>
        </Card>
      )}

      <PalangDaftar daftar={daftar} placeholder="Cari opsi / kode / jenis…" testId="oh-palang"
                    ekspor={{ nama: "opsi_harga", kolom: daftar.kolom }} />
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
            <tr><ThUrut daftar={daftar} kunci="jenis_opsi" className="text-left px-3 py-2">Jenis</ThUrut>
              <ThUrut daftar={daftar} kunci="label" className="text-left px-3 py-2">Opsi</ThUrut>
              <th className="text-left px-3 py-2">Berlaku</th>
              <ThUrut daftar={daftar} kunci="tambahan_idr" className="text-right px-3 py-2" align="right">Tambahan / lembar</ThUrut>
              <ThUrut daftar={daftar} kunci="aktif" className="text-left px-3 py-2">Status</ThUrut><th className="text-right px-3 py-2">Aksi</th></tr>
          </thead>
          <tbody>
            {daftar.total === 0 && <tr><td colSpan={6} className="px-3 py-4 text-muted-foreground">
              {daftar.cari.trim() ? "Tidak ada yang cocok." : "Belum ada opsi."}</td></tr>}
            {daftar.tampil.map(o => (
              <tr key={o.id} className="border-t" data-testid={`oh-baris-${o.kode}`}>
                <td className="px-3 py-2 text-xs">{LABEL_JENIS_OPSI[o.jenis_opsi] || o.jenis_opsi}</td>
                <td className="px-3 py-2">{o.label}<div className="font-mono text-xs text-muted-foreground">{o.kode}</div></td>
                <td className="px-3 py-2 text-xs">{o.region_id ? (o.region_id === profile?.region_id ? "Wilayah Anda" : "Wilayah lain") : "Semua wilayah"}</td>
                <td className="px-3 py-2 text-right font-mono">{formatIDR(o.tambahan_idr)}</td>
                <td className="px-3 py-2">{o.aktif ? <Badge variant="secondary">Aktif</Badge> : <Badge variant="outline">Nonaktif</Badge>}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap space-x-1">
                  {bolehUbah(o) && <>
                    <Button size="sm" variant="outline" disabled={sibuk === o.id} onClick={() => ubahTarif(o)} data-testid={`oh-tarif-${o.kode}`}>Ubah tarif</Button>
                    <Button size="sm" variant="outline" disabled={sibuk === o.id}
                            onClick={() => ubah(o, { aktif: !o.aktif }, o.aktif ? "Opsi dinonaktifkan." : "Opsi diaktifkan kembali.")}>
                      {o.aktif ? "Nonaktifkan" : "Aktifkan"}</Button>
                  </>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Paginasi daftar={daftar} testId="oh-paginasi" />
      </Card>
    </div>
  );
}
